/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invoices Service - Invoice Management API
 * ============================================================================
 *
 * Service layer for invoice management API calls.
 * Handles invoice CRUD operations, PDF generation, and status management.
 *
 * ============================================================================
 */

import apiClient, { get, post, put } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, transformPageResponse } from '@/services/api/types';
import { Invoice, InvoiceStatus, InvoiceFilterParams, InvoicePreview } from '@/types/superadmin.types';
import { saveBlob } from '@/utils/download';

/**
 * Create invoice request
 */
export interface CreateInvoiceRequest {
  issueImmediately?: boolean;
  dueDays?: number;
  notes?: string;
  lineItems?: CreateInvoiceLineItemRequest[];
}

/**
 * Create invoice line item request
 */
export interface CreateInvoiceLineItemRequest {
  description: string;
  quantity: number;
  unitPriceUsd: number;
  moduleCode?: string;
  tierCode?: string;
}

/**
 * Issue invoice request
 */
export interface IssueInvoiceRequest {
  dueInDays?: number;
  issueDate?: string;
  sendEmail?: boolean;
}

/**
 * Invoice statistics
 */
export interface InvoiceStats {
  totalInvoices: number;
  draftCount: number;
  issuedCount: number;
  paidCount: number;
  overdueCount: number;
  cancelledCount: number;
  totalAmountUsd: number;
  paidAmountUsd: number;
  outstandingAmountUsd: number;
}

const BASE_URL = '/billing/invoices';

/**
 * Invoices service
 */
export const invoicesService = {
  /**
   * Get all invoices with pagination (SuperAdmin only)
   */
  async getAllInvoices(
    page = 0,
    size = 20,
    sort = 'createdAt,desc'
  ): Promise<PaginatedResponse<Invoice>> {
    const response = await get<ApiResponse<PaginatedResponse<Invoice>>>(
      `${BASE_URL}?page=${page}&size=${size}&sort=${sort}`
    );
    return transformPageResponse(response.data);
  },

  /**
   * Get invoices for a specific organization
   */
  async getOrganizationInvoices(
    orgId: string,
    page = 0,
    size = 20,
    sort = 'createdAt,desc'
  ): Promise<PaginatedResponse<Invoice>> {
    const response = await get<ApiResponse<PaginatedResponse<Invoice>>>(
      `${BASE_URL}/organization/${orgId}?page=${page}&size=${size}&sort=${sort}`
    );
    return transformPageResponse(response.data);
  },

  /**
   * Search invoices with filters
   */
  async searchInvoices(
    filters: InvoiceFilterParams & { page?: number; size?: number; sort?: string }
  ): Promise<PaginatedResponse<Invoice>> {
    const params = new URLSearchParams();

    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);

    params.append('page', String(filters.page ?? 0));
    params.append('size', String(filters.size ?? 20));
    params.append('sort', filters.sort ?? 'createdAt,desc');

    const response = await get<ApiResponse<PaginatedResponse<Invoice>>>(
      `${BASE_URL}/search?${params.toString()}`
    );
    return transformPageResponse(response.data);
  },

  /**
   * Get invoice by ID
   */
  async getInvoiceById(id: string): Promise<Invoice> {
    const response = await get<ApiResponse<Invoice>>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Get invoice by invoice number
   */
  async getInvoiceByNumber(invoiceNumber: string): Promise<Invoice> {
    const response = await get<ApiResponse<Invoice>>(`${BASE_URL}/by-number/${invoiceNumber}`);
    return response.data;
  },

  /**
   * Create invoice from subscription
   */
  async createFromSubscription(
    subscriptionId: string,
    request?: CreateInvoiceRequest
  ): Promise<Invoice> {
    const response = await post<ApiResponse<Invoice>>(
      `${BASE_URL}/from-subscription/${subscriptionId}`,
      request || {}
    );
    return response.data;
  },

  /**
   * Create manual invoice for an organization
   */
  async createManualInvoice(
    orgId: string,
    request: CreateInvoiceRequest
  ): Promise<Invoice> {
    const response = await post<ApiResponse<Invoice>>(
      `${BASE_URL}/organization/${orgId}`,
      request
    );
    return response.data;
  },

  /**
   * Issue a draft invoice
   */
  async issueInvoice(id: string, request?: IssueInvoiceRequest): Promise<Invoice> {
    const response = await put<ApiResponse<Invoice>>(
      `${BASE_URL}/${id}/issue`,
      request || {}
    );
    return response.data;
  },

  /**
   * Cancel an invoice
   */
  async cancelInvoice(id: string, reason?: string): Promise<Invoice> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await put<ApiResponse<Invoice>>(
      `${BASE_URL}/${id}/cancel${params}`,
      {}
    );
    return response.data;
  },

  /**
   * Mark invoice as paid
   */
  async markAsPaid(id: string, paymentId?: string): Promise<Invoice> {
    const params = paymentId ? `?paymentId=${paymentId}` : '';
    const response = await put<ApiResponse<Invoice>>(
      `${BASE_URL}/${id}/mark-paid${params}`,
      {}
    );
    return response.data;
  },

  /**
   * Void an invoice (SuperAdmin only)
   */
  async voidInvoice(id: string, reason: string): Promise<Invoice> {
    const response = await put<ApiResponse<Invoice>>(
      `${BASE_URL}/${id}/void?reason=${encodeURIComponent(reason)}`,
      {}
    );
    return response.data;
  },

  /**
   * Generate PDF for an invoice
   */
  async generatePdf(id: string): Promise<Invoice> {
    const response = await post<ApiResponse<Invoice>>(
      `${BASE_URL}/${id}/generate-pdf`,
      {}
    );
    return response.data;
  },

  /**
   * Get PDF download URL for an invoice
   */
  getPdfDownloadUrl(id: string): string {
    return `/api/v1${BASE_URL}/${id}/pdf`;
  },

  /**
   * Download invoice PDF as blob with authentication
   * Triggers browser download
   */
  async downloadPdf(id: string, filename?: string): Promise<void> {
    const response = await apiClient.get(`${BASE_URL}/${id}/pdf`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let downloadFilename = filename || `invoice_${id}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Trigger download with reliable filename handling
    await saveBlob([response.data], downloadFilename, 'application/pdf');
  },

  /**
   * Get invoice preview with HTML content (theme-aware)
   *
   * @param id Invoice ID
   * @param theme Theme preference ('light' or 'dark')
   * @param language Language code ('en' or 'fr')
   */
  async getInvoicePreview(
    id: string,
    theme: 'light' | 'dark' = 'dark',
    language = 'en'
  ): Promise<InvoicePreview> {
    const response = await apiClient.get<ApiResponse<InvoicePreview>>(
      `${BASE_URL}/${id}/preview`,
      {
        params: { theme },
        headers: { 'Accept-Language': language },
      }
    );
    return response.data.data;
  },

  /**
   * Get invoice HTML by variant
   *
   * @param id Invoice ID
   * @param variant Template variant ('blackops', 'hybrid', 'tactical', 'stealth')
   * @param language Language code ('en' or 'fr')
   */
  async getInvoiceHtml(
    id: string,
    variant: 'blackops' | 'hybrid' | 'tactical' | 'stealth',
    language = 'en'
  ): Promise<string> {
    const response = await apiClient.get<string>(
      `${BASE_URL}/${id}/html/${variant}`,
      {
        headers: { 'Accept-Language': language },
        responseType: 'text' as any,
      }
    );
    return response.data;
  },

  /**
   * Download PDF with specific variant
   *
   * @param id Invoice ID
   * @param variant Template variant
   * @param language Language code
   * @param filename Optional custom filename
   */
  async downloadPdfVariant(
    id: string,
    variant: 'blackops' | 'hybrid' | 'tactical' | 'stealth',
    language = 'en',
    filename?: string
  ): Promise<void> {
    const response = await apiClient.get(`${BASE_URL}/${id}/pdf/${variant}`, {
      headers: { 'Accept-Language': language },
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let downloadFilename = filename || `invoice_${id}_${variant}_${language}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Trigger download with reliable filename handling
    await saveBlob([response.data], downloadFilename, 'application/pdf');
  },

  /**
   * Get invoice statistics
   */
  async getInvoiceStats(orgId?: string): Promise<InvoiceStats> {
    const params = orgId ? `?orgId=${orgId}` : '';
    const response = await get<ApiResponse<InvoiceStats>>(`${BASE_URL}/stats${params}`);
    return response.data;
  },

  /**
   * Get overdue invoices (SuperAdmin only)
   */
  async getOverdueInvoices(
    page = 0,
    size = 20
  ): Promise<PaginatedResponse<Invoice>> {
    const response = await get<ApiResponse<PaginatedResponse<Invoice>>>(
      `${BASE_URL}/overdue?page=${page}&size=${size}`
    );
    return transformPageResponse(response.data);
  },

  /**
   * Check and update payment status for an invoice's linked payment.
   * Calls the payment status check endpoint if the invoice has a paymentId.
   *
   * @param paymentId The payment UUID linked to the invoice
   * @returns Payment status result from the gateway
   */
  async checkPaymentStatus(paymentId: string): Promise<PaymentStatusResult> {
    const response = await post<ApiResponse<PaymentStatusResult>>(
      `/payments/${paymentId}/check-status`,
      {}
    );
    return response.data;
  },
};

/**
 * Payment status result from gateway check
 */
export interface PaymentStatusResult {
  transactionId: string;
  status: string;
  gatewayStatus?: string;
  message?: string;
  paidAt?: string;
  amount?: number;
  currency?: string;
  metadata?: Record<string, unknown>;
}

export default invoicesService;
